/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart } from 'lucide-react';
import { useTheme } from '../theme';

interface Item {
  id: number;
  storeId?: number;
  title: string;
  price: number;
  image: string;
  distance?: string;
  rating?: number;
}

interface ItemCardProps {
  item: Item;
  isLarge?: boolean;
}

export const ItemCard: React.FC<ItemCardProps> = ({ item, isLarge = false }) => {
  const theme = useTheme();
  const [isSaved, setIsSaved] = useState(false);

  return (
    <Link 
      to={`/item/${item.id}`}
      className="group block cursor-pointer transition-all hover:shadow-lg"
      style={{
        background: theme.colors.surface,
        borderRadius: `${theme.radius.lg}px`,
        padding: isLarge ? `${theme.spacing.lg}px` : `${theme.spacing.md}px`,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.colors.border}`,
        color: theme.colors.text,
        transform: 'translateZ(0)',
        textDecoration: 'none'
      }}
    >
      <div className="relative overflow-hidden" style={{ borderRadius: `${theme.radius.md}px` }}>
        <img
          src={item.image || 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=400&h=400'}
          alt={item.title}
          className="w-full transition-transform duration-300 group-hover:scale-105"
          style={{
            height: isLarge ? '300px' : '200px',
            objectFit: 'cover',
          }}
        />
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsSaved(!isSaved);
          }}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors"
          style={{ color: isSaved ? theme.colors.danger : theme.colors.mutedText }}
        >
          <Heart size={18} fill={isSaved ? theme.colors.danger : 'none'} />
        </button>
      </div>
      
      <div className="mt-3 space-y-1">
        <div className="flex justify-between items-start">
          <div className="font-bold text-lg" style={{ color: theme.colors.text }}>
            ${item.price.toLocaleString()}
          </div>
          {item.rating && (
            <div className="flex items-center gap-0.5 text-sm font-medium" style={{ color: theme.colors.accent }}>
              <Star size={14} fill={theme.colors.accent} />
              <span>{item.rating.toFixed(1)}</span>
            </div>
          )}
        </div>
        
        <h3 className="font-medium truncate group-hover:text-blue-600 transition-colors" style={{ color: theme.colors.text }}>
          {item.title}
        </h3>
        
        {item.distance && (
          <p className="text-sm flex items-center gap-1" style={{ color: theme.colors.mutedText }}>
            <span>{item.distance}</span>
          </p>
        )}
      </div>
    </Link>
  );
};
